import { Button, Group, MultiSelect, SegmentedControl, Select, SimpleGrid, Stack, Switch, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import type { LocationRow } from '../api/types';
import { nameOf, tr } from '../lib/i18n';
import { Panel } from '../ui/kit';
import { useToast } from './Toast';

/** A company's «إعدادات الخريطة» (same as Settings → الخريطة in its own system), set by the platform. */
export interface OrgMapSettingsValue {
  workGovernorateIds: number[];
  workAreaIds: number[];
  lockToWorkArea: boolean;
  style: 'satellite' | 'streets';
  colorBy: 'kind' | 'ppm';
  showApprox: boolean;
  rentPeriod: 'year' | 'month';
  sharePrecision: 'exact' | 'approx' | 'hidden';
}
const DEFAULTS: OrgMapSettingsValue = {
  workGovernorateIds: [], workAreaIds: [], lockToWorkArea: false, style: 'satellite', colorBy: 'kind', showApprox: true, rentPeriod: 'year', sharePrecision: 'approx',
};

export function OrgMapSettings({ orgId, countryId, value }: { orgId: string; countryId: number; value: Partial<OrgMapSettingsValue> | undefined }) {
  const qc = useQueryClient();
  const { toast, toastError } = useToast();
  const saved = useMemo(() => ({ ...DEFAULTS, ...(value ?? {}) }), [value]);
  const [m, setM] = useState<OrgMapSettingsValue>(saved);
  useEffect(() => setM(saved), [saved]);

  const govs = useQuery({
    queryKey: ['loc', 'governorates', countryId],
    queryFn: () => api<{ items: LocationRow[] }>('/governorates', { query: { parentId: countryId, includeInactive: false } }),
  });
  const areas = useQuery({
    queryKey: ['loc', 'areas-of-country', countryId],
    enabled: !!govs.data?.items.length,
    queryFn: async () => {
      const all = await Promise.all((govs.data?.items ?? []).map((g) => api<{ items: LocationRow[] }>('/areas', { query: { parentId: g.id, includeInactive: false } }).then((r) => ({ g, items: r.items }))));
      return all;
    },
  });

  const save = useMutation({
    mutationFn: () => api(`/organizations/${orgId}`, { method: 'PATCH', body: { map: m } }),
    onSuccess: () => {
      toast(tr('تم حفظ إعدادات الخريطة', 'Map settings saved'));
      void qc.invalidateQueries({ queryKey: ['organization', orgId] });
    },
    onError: toastError,
  });

  // areas of governorates already chosen as a whole are covered — not offered again
  const areaData = (areas.data ?? [])
    .filter(({ g }) => !m.workGovernorateIds.includes(g.id))
    .map(({ g, items }) => ({ group: nameOf(g), items: items.map((a) => ({ value: String(a.id), label: nameOf(a) })) }))
    .filter((x) => x.items.length);

  return (
    <Panel title={tr('إعدادات الخريطة', 'Map settings')} description={tr('منطقة عمل الشركة (الخريطة والبحث يبقيان داخلها) والإعدادات الافتراضية. بدون اختيار = كل الدولة.', 'The company’s work area (the map and search stay inside) and defaults. Nothing chosen = the whole country.')}>
      <Stack gap="md">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          <MultiSelect
            label={tr('محافظات كاملة', 'Whole governorates')}
            searchable
            clearable
            data={(govs.data?.items ?? []).map((g) => ({ value: String(g.id), label: nameOf(g) }))}
            value={m.workGovernorateIds.map(String)}
            onChange={(v) => setM({ ...m, workGovernorateIds: v.map(Number) })}
          />
          <MultiSelect
            label={tr('مناطق محددة', 'Chosen areas')}
            searchable
            clearable
            data={areaData}
            value={m.workAreaIds.map(String)}
            onChange={(v) => setM({ ...m, workAreaIds: v.map(Number) })}
            nothingFoundMessage={tr('لا نتائج', 'No results')}
          />
        </SimpleGrid>
        <Switch checked={m.lockToWorkArea} onChange={(e) => setM({ ...m, lockToWorkArea: e.currentTarget.checked })} label={tr('ثبّت الخريطة داخل منطقة العمل', 'Keep the map inside the work area')} />
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Stack gap={4}>
            <Text size="sm">{tr('شكل الخريطة', 'Map style')}</Text>
            <SegmentedControl value={m.style} onChange={(v) => setM({ ...m, style: v as OrgMapSettingsValue['style'] })} data={[{ value: 'satellite', label: tr('قمر صناعي', 'Satellite') }, { value: 'streets', label: tr('شوارع', 'Streets') }]} />
          </Stack>
          <Stack gap={4}>
            <Text size="sm">{tr('لون الدبابيس', 'Pin colours')}</Text>
            <SegmentedControl value={m.colorBy} onChange={(v) => setM({ ...m, colorBy: v as OrgMapSettingsValue['colorBy'] })} data={[{ value: 'kind', label: tr('بيع / إيجار', 'Sale / rent') }, { value: 'ppm', label: tr('سعر المتر', 'Per m²') }]} />
          </Stack>
          <Stack gap={4}>
            <Text size="sm">{tr('سعر الإيجار', 'Rent price')}</Text>
            <SegmentedControl value={m.rentPeriod} onChange={(v) => setM({ ...m, rentPeriod: v as OrgMapSettingsValue['rentPeriod'] })} data={[{ value: 'year', label: tr('سنوي', 'Yearly') }, { value: 'month', label: tr('شهري', 'Monthly') }]} />
          </Stack>
          <Select
            label={tr('الموقع في صفحة مشاركة العقار', 'Location on a shared property')}
            value={m.sharePrecision}
            allowDeselect={false}
            onChange={(v) => v && setM({ ...m, sharePrecision: v as OrgMapSettingsValue['sharePrecision'] })}
            data={[
              { value: 'approx', label: tr('تقريبي (دائرة)', 'Approximate (circle)') },
              { value: 'exact', label: tr('دقيق', 'Exact') },
              { value: 'hidden', label: tr('مخفي', 'Hidden') },
            ]}
          />
        </SimpleGrid>
        <Switch checked={m.showApprox} onChange={(e) => setM({ ...m, showApprox: e.currentTarget.checked })} label={tr('أظهر العقارات بدون موقع دقيق في منتصف منطقتها', 'Show properties without an exact pin in the middle of their area')} />
        <Group justify="flex-end">
          <Button disabled={JSON.stringify(m) === JSON.stringify(saved)} loading={save.isPending} onClick={() => save.mutate()}>{tr('حفظ', 'Save')}</Button>
        </Group>
      </Stack>
    </Panel>
  );
}
